import { useCallback, useMemo, useReducer } from 'react';
import { toaster } from '@optiaxiom/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useInstanceId, useNamespacedNavigate } from '@frontend/hooks';
import type { AgentVersionSummary, SpecializedAgent } from '@frontend/types';

import {
  createSpecializedAgent,
  getSpecializedAgentVersion,
  getSpecializedAgentVersions,
  updateSpecializedAgent,
} from '../../services/specialized-agent';

import { initialState, reducer, type VersionToRestore } from './reducer';

export function getVersionDisplayLabel(
  version: AgentVersionSummary,
  totalVersions: number,
  index: number
): string {
  // there's two different fields: version (the user facing string) and internal_version
  // while hypatia manages internal_version on its own, the version can be modified with a PATCH
  // however, the version field wasn't being updated with revisions to the agent_version
  // so all versions have a 1.0.0 value
  // the logic below accounts for this discrepancy

  // if there are multiple versions and agent_version is "1.0.0",
  // it means the version wasn't properly updated, so show index number
  if (version.agent_version === '1.0.0') {
    return `V${totalVersions - index}`;
  }

  // otherwise, agent_version was properly updated, so show it
  return `${version.agent_version}`;
}

type SelectVersionResult = {
  version: AgentVersionSummary | null;
  displayLabel: string;
  isCurrentVersion: boolean;
};

export type NameDialog = {
  isOpen: boolean;
  name: string;
  setName: (name: string) => void;
  onOpen: (version: AgentVersionSummary, displayLabel: string) => void;
  onClose: () => void;
  onSave: () => void;
  isLoading: boolean;
};

export type RestoreDialog = {
  isOpen: boolean;
  versionToRestore: VersionToRestore;
  onOpen: (version: AgentVersionSummary, displayLabel: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
};

export type UseAgentVersionsReturn = ReturnType<typeof useAgentVersions>;

export function useAgentVersions(agentId: string) {
  const { instanceId } = useInstanceId();
  const queryClient = useQueryClient();
  const navigate = useNamespacedNavigate();

  const [state, dispatch] = useReducer(reducer, initialState);

  const { data, isLoading, error } = useQuery({
    queryKey: getSpecializedAgentVersions.key(instanceId ?? '', agentId),
    queryFn: getSpecializedAgentVersions(instanceId ?? '', agentId),
    enabled: !!instanceId && !!agentId,
  });

  const versions = useMemo(() => data?.versions ?? [], [data?.versions]);

  const versionUrl = state.selection?.versionSummary?.links?.self ?? '';
  const { data: selectedVersionData } = useQuery({
    queryKey: getSpecializedAgentVersion.key(versionUrl),
    queryFn: getSpecializedAgentVersion(versionUrl),
    enabled: !!versionUrl,
  });

  const selectedVersionDisplayLabel = useMemo(() => {
    if (!state.selection) return '';
    const index = versions.findIndex(v => v.internal_version === state.selection?.versionNumber);
    if (index === -1 || index === 0) return ''; // -1 = not found, 0 = current version
    return getVersionDisplayLabel(versions[index], versions.length, index);
  }, [state.selection, versions]);

  const { mutate: duplicateAgent } = useMutation({
    mutationFn: async ({
      version,
      displayLabel,
    }: {
      version: AgentVersionSummary;
      displayLabel: string;
    }) => {
      if (!version.links?.self || !instanceId) {
        throw new Error('Missing version URL or instance ID');
      }

      const fullAgent = await getSpecializedAgentVersion(version.links.self)();

      const copiedAgent: SpecializedAgent = {
        ...fullAgent,
        name: `${fullAgent.name} ${displayLabel}`,
        agent_id: `${fullAgent.agent_id}_copy_${new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14)}`,
        locked_fields: [],
        is_system_agent: false,
        is_enabled_in_chat: false,
        baseline_evaluation_score: null,
      };

      return createSpecializedAgent(copiedAgent, instanceId)();
    },
    onSuccess: (response, { version }) => {
      queryClient.invalidateQueries({ queryKey: ['opal:specialized-agents'] });
      toaster.create(`Agent "${version.name}" duplicated as "${response.data.name}" successfully`, {
        intent: 'success',
      });
      dispatch({ type: 'RESET_SELECTION' });
      navigate(`/agents/specialized/${response.data.id}`);
    },
    onError: () => {
      toaster.create('Failed to duplicate agent', { intent: 'danger' });
    },
  });

  const { mutate: restoreAgent, isPending: isRestoring } = useMutation({
    mutationFn: async ({ version }: { version: AgentVersionSummary; displayLabel: string }) => {
      if (!version.links?.self || !version.links?.agent) {
        throw new Error('Missing version or agent URL');
      }

      const fullAgent = await getSpecializedAgentVersion(version.links.self)();

      return updateSpecializedAgent(version.links.agent, fullAgent)();
    },
    onSuccess: (_, { displayLabel }) => {
      queryClient.invalidateQueries({
        queryKey: getSpecializedAgentVersions.key(instanceId ?? '', agentId),
      });
      queryClient.invalidateQueries({ queryKey: ['opal:specialized-agents'] });
      queryClient.invalidateQueries({ queryKey: ['opal:specialized-agent'] });
      toaster.create(`Agent restored to ${displayLabel} successfully`, {
        intent: 'success',
      });
      dispatch({ type: 'RESTORE_SUCCESS' });
    },
    onError: () => {
      toaster.create('Failed to restore agent version', { intent: 'danger' });
    },
  });

  const resetSelection = useCallback(() => {
    dispatch({ type: 'RESET_SELECTION' });
  }, []);

  const handleDuplicate = useCallback(
    (version: AgentVersionSummary, displayLabel: string) => {
      duplicateAgent({ version, displayLabel });
    },
    [duplicateAgent]
  );

  const selectVersion = useCallback(
    (version: AgentVersionSummary, index: number): SelectVersionResult => {
      const isCurrentVersion =
        versions.length > 0 && version.internal_version === versions[0].internal_version;
      const displayLabel = getVersionDisplayLabel(version, versions.length, index);

      dispatch({
        type: 'SELECT_VERSION',
        versionNumber: version.internal_version,
        versionSummary: isCurrentVersion ? null : version,
      });

      return {
        version: isCurrentVersion ? null : version,
        displayLabel,
        isCurrentVersion,
      };
    },
    [versions]
  );

  const restoreDialog: RestoreDialog = useMemo(
    () => ({
      isOpen: state.restoreDialog.isOpen,
      versionToRestore: state.restoreDialog.versionToRestore,
      onOpen: (version: AgentVersionSummary, displayLabel: string) => {
        dispatch({ type: 'OPEN_RESTORE_DIALOG', version, displayLabel });
      },
      onConfirm: () => {
        if (!state.restoreDialog.versionToRestore) return;
        restoreAgent({
          version: state.restoreDialog.versionToRestore.version,
          displayLabel: state.restoreDialog.versionToRestore.displayLabel,
        });
      },
      onCancel: () => dispatch({ type: 'CLOSE_RESTORE_DIALOG' }),
      isLoading: isRestoring,
    }),
    [state.restoreDialog, isRestoring, restoreAgent]
  );

  return {
    // Core data
    versions,
    isLoading,
    error,

    // Selection state
    selectedVersionSummary: state.selection?.versionSummary ?? null,
    selectedVersionData,
    selectedVersionDisplayLabel,
    selectVersion,
    resetSelection,

    // Actions
    handleDuplicate,

    // Dialogs
    restoreDialog,
  };
}
